import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  GridLegacy as Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Avatar,
  Tooltip,
  Menu,
  MenuList,
  ClickAwayListener,
  Popper,
  Grow,
  Switch,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../types/userTypes';
import productService from '../services/productService';
import { Product, ProductFormData, ProductFilters, ProductStats, LoyaltyLevel, ProductDownload } from '../types/productTypes';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const AdminProductManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // State management
  const [products, setProducts] = useState<Product[]>([]);
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDownloadsDialogOpen, setIsDownloadsDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [downloadRecords, setDownloadRecords] = useState<ProductDownload[]>([]);
  const [downloadAnalytics, setDownloadAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Filter states
  const [filters, setFilters] = useState<ProductFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    loyaltyLevels: [],
  });
  const [formLoading, setFormLoading] = useState(false);

  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuProduct, setMenuProduct] = useState<Product | null>(null);

  // Check permissions - fallback to content_management or admin status
  const canManageProducts = userProfile && (
    (userProfile.role && hasPermission(userProfile.role, 'product_management', userProfile.adminPermissions)) ||
    (userProfile.role && hasPermission(userProfile.role, 'content_management', userProfile.adminPermissions)) ||
    userProfile.isAdmin === true
  );

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) {
        setProfileLoading(false);
        return;
      }
      
      try {
        setProfileLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  useEffect(() => {
    // Don't redirect while profile is still loading
    if (profileLoading) return;
    
    if (!canManageProducts) {
      console.log('No permission to manage products. UserProfile:', userProfile);
      navigate('/admin');
      return;
    }
    loadData();
  }, [canManageProducts, navigate, profileLoading, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, statsData] = await Promise.all([
        productService.getProducts(filters),
        productService.getProductStats()
      ]);
      setProducts(productsData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (newFilters: ProductFilters) => {
    setFilters(newFilters);
    try {
      const productsData = await productService.getProducts(newFilters);
      setProducts(productsData);
    } catch (err) {
      console.error('Error filtering products:', err);
      setError('Failed to filter products');
    }
  };

  const handleCreateProduct = async () => {
    if (!currentUser?.uid) return;

    try {
      setFormLoading(true);
      await productService.createProduct(formData, currentUser.uid);
      setSuccess('Product created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      console.error('Error creating product:', err);
      setError('Failed to create product');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditProduct = async () => {
    if (!currentUser?.uid || !selectedProduct) return;

    try {
      setFormLoading(true);
      await productService.updateProduct(selectedProduct.id, formData, currentUser.uid);
      setSuccess('Product updated successfully');
      setIsEditDialogOpen(false);
      resetForm();
      setSelectedProduct(null);
      loadData();
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      setFormLoading(true);
      await productService.deleteProduct(selectedProduct.id);
      setSuccess('Product deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
      loadData();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (product: Product) => {
    if (!currentUser?.uid) return;

    try {
      await productService.toggleProductStatus(product.id, currentUser.uid);
      setSuccess(`Product ${product.isActive ? 'deactivated' : 'activated'} successfully`);
      loadData();
    } catch (err) {
      console.error('Error toggling product status:', err);
      setError('Failed to update product status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      loyaltyLevels: [],
    });
  };

  const openEditDialog = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      loyaltyLevels: product.loyaltyLevels,
    });
    setIsEditDialogOpen(true);
    handleMenuClose();
  };

  const openDeleteDialog = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: Product) => {
    setAnchorEl(event.currentTarget);
    setMenuProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuProduct(null);
  };

  const handleViewDownloads = async () => {
    try {
      setAnalyticsLoading(true);
      setIsDownloadsDialogOpen(true);
      const [downloads, analytics] = await Promise.all([
        productService.getAllDownloads(),
        productService.getDownloadAnalytics(30)
      ]);
      setDownloadRecords(downloads);
      setDownloadAnalytics(analytics);
    } catch (err) {
      console.error('Error loading downloads:', err);
      setError('Failed to load download analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleViewProductDownloads = async (product: Product) => {
    try {
      setSelectedProduct(product);
      setAnalyticsLoading(true);
      setIsDownloadsDialogOpen(true);
      const downloads = await productService.getProductDownloads(product.id);
      setDownloadRecords(downloads);
    } catch (err) {
      console.error('Error loading product downloads:', err);
      setError('Failed to load product downloads');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const getLevelColor = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#666';
    }
  };

  const getLevelIcon = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Show loading while profile is loading
  if (profileLoading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!canManageProducts) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page.
          <br />
          Required: Product Management permission or Admin status
          <br />
          Your role: {userProfile?.role || 'Not set'}
          <br />
          Is Admin: {userProfile?.isAdmin ? 'Yes' : 'No'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Product Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 2 }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleViewDownloads}
            sx={{ mr: 2 }}
          >
            View Downloads
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            Add Product
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Products
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalProducts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Active Products
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.activeProducts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Downloads
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {stats.totalDownloads}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    By Level
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${getLevelIcon('bronze')} ${stats.productsByLevel.bronze}`}
                      size="small"
                      sx={{ bgcolor: `${getLevelColor('bronze')}20` }}
                    />
                    <Chip
                      label={`${getLevelIcon('silver')} ${stats.productsByLevel.silver}`}
                      size="small"
                      sx={{ bgcolor: `${getLevelColor('silver')}20` }}
                    />
                    <Chip
                      label={`${getLevelIcon('gold')} ${stats.productsByLevel.gold}`}
                      size="small"
                      sx={{ bgcolor: `${getLevelColor('gold')}20` }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Search products"
                variant="outlined"
                size="small"
                value={filters.searchQuery || ''}
                onChange={(e) => handleFilterChange({ ...filters, searchQuery: e.target.value })}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ minWidth: 250 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Loyalty Level</InputLabel>
                <Select
                  value={filters.loyaltyLevel || ''}
                  label="Loyalty Level"
                  onChange={(e) => handleFilterChange({ ...filters, loyaltyLevel: e.target.value as LoyaltyLevel || undefined })}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  <MenuItem value="bronze">{getLevelIcon('bronze')} Bronze</MenuItem>
                  <MenuItem value="silver">{getLevelIcon('silver')} Silver</MenuItem>
                  <MenuItem value="gold">{getLevelIcon('gold')} Gold</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive?.toString() || ''}
                  label="Status"
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange({ 
                      ...filters, 
                      isActive: value === '' ? undefined : value === 'true' 
                    });
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={() => handleFilterChange({})}
                size="small"
              >
                Clear Filters
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Loyalty Levels</TableCell>
                    <TableCell>Downloads</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 6 }).map((_, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body1" color="text.secondary">
                          No products found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {product.imageUrl ? (
                              <Avatar
                                src={product.imageUrl}
                                alt={product.name}
                                sx={{ width: 50, height: 50 }}
                                variant="rounded"
                              />
                            ) : (
                              <Avatar
                                sx={{ 
                                  width: 50, 
                                  height: 50, 
                                  bgcolor: 'grey.200',
                                  color: 'grey.600'
                                }}
                                variant="rounded"
                              >
                                📦
                              </Avatar>
                            )}
                            <Box>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {product.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {product.description.length > 60 
                                  ? `${product.description.substring(0, 60)}...` 
                                  : product.description}
                              </Typography>
                              {product.fileSize && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(product.fileSize)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {product.loyaltyLevels.map((level) => (
                              <Chip
                                key={level}
                                label={`${getLevelIcon(level)} ${level}`}
                                size="small"
                                sx={{ 
                                  bgcolor: `${getLevelColor(level)}20`,
                                  color: getLevelColor(level),
                                  fontWeight: 'medium',
                                  textTransform: 'capitalize'
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DownloadIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {product.downloadCount}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={product.isActive ? 'Active' : 'Inactive'}
                            color={product.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.createdAt?.toDate ? 
                              product.createdAt.toDate().toLocaleDateString() : 
                              new Date(product.createdAt).toLocaleDateString()
                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, product)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => openEditDialog(menuProduct!)}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleToggleStatus(menuProduct!)}>
            {menuProduct?.isActive ? (
              <>
                <VisibilityOffIcon sx={{ mr: 1 }} fontSize="small" />
                Deactivate
              </>
            ) : (
              <>
                <ViewIcon sx={{ mr: 1 }} fontSize="small" />
                Activate
              </>
            )}
          </MenuItem>
          <MenuItem onClick={() => openDeleteDialog(menuProduct!)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </Menu>

        {/* Create Product Dialog */}
        <ProductFormDialog
          open={isCreateDialogOpen}
          onClose={() => {
            setIsCreateDialogOpen(false);
            resetForm();
          }}
          onSubmit={handleCreateProduct}
          formData={formData}
          setFormData={setFormData}
          loading={formLoading}
          title="Create New Product"
        />

        {/* Edit Product Dialog */}
        <ProductFormDialog
          open={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            resetForm();
            setSelectedProduct(null);
          }}
          onSubmit={handleEditProduct}
          formData={formData}
          setFormData={setFormData}
          loading={formLoading}
          title="Edit Product"
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
          <DialogTitle>Delete Product</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedProduct?.name}"? 
              This action cannot be undone and will also delete associated files.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteProduct} 
              color="error" 
              variant="contained"
              disabled={formLoading}
            >
              {formLoading ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Downloads Analytics Dialog */}
        <Dialog open={isDownloadsDialogOpen} onClose={() => setIsDownloadsDialogOpen(false)}>
          <DialogTitle>Product Downloads</DialogTitle>
          <DialogContent>
            {analyticsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  Downloads Analytics
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Total Downloads: {downloadAnalytics?.totalDownloads}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Downloads in the last 30 days: {downloadAnalytics?.downloadsLast30Days}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Downloads by level:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${getLevelIcon('bronze')} ${downloadAnalytics?.downloadsByLevel.bronze}`}
                    size="small"
                    sx={{ bgcolor: `${getLevelColor('bronze')}20` }}
                  />
                  <Chip
                    label={`${getLevelIcon('silver')} ${downloadAnalytics?.downloadsByLevel.silver}`}
                    size="small"
                    sx={{ bgcolor: `${getLevelColor('silver')}20` }}
                  />
                  <Chip
                    label={`${getLevelIcon('gold')} ${downloadAnalytics?.downloadsByLevel.gold}`}
                    size="small"
                    sx={{ bgcolor: `${getLevelColor('gold')}20` }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDownloadsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

// Product Form Dialog Component
interface ProductFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: ProductFormData;
  setFormData: (data: ProductFormData) => void;
  loading: boolean;
  title: string;
}

const ProductFormDialog: React.FC<ProductFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  loading,
  title,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Show compression state for image files
      if (file.type.startsWith('image/')) {
        setCompressing(true);
        
        // Simulate compression time (the actual compression happens in the service)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setCompressing(false);
      }
      
      setFormData({ ...formData, imageFile: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownloadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData({ ...formData, downloadFile: file });
    }
  };

  const handleLoyaltyLevelChange = (level: LoyaltyLevel, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        loyaltyLevels: [...formData.loyaltyLevels, level]
      });
    } else {
      setFormData({
        ...formData,
        loyaltyLevels: formData.loyaltyLevels.filter(l => l !== level)
      });
    }
  };

  const getLevelColor = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#666';
    }
  };

  const getLevelIcon = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '';
    }
  };

  const isFormValid = formData.name.trim() && formData.description.trim() && formData.loyaltyLevels.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <TextField
              label="Product Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              error={!formData.name.trim()}
              helperText={!formData.name.trim() ? 'Product name is required' : ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              error={!formData.description.trim()}
              helperText={!formData.description.trim() ? 'Description is required' : ''}
            />
          </Grid>

          {/* Loyalty Levels */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Available for Loyalty Levels
            </Typography>
            <FormGroup row>
              {(['bronze', 'silver', 'gold'] as LoyaltyLevel[]).map((level) => (
                <FormControlLabel
                  key={level}
                  control={
                    <Checkbox
                      checked={formData.loyaltyLevels.includes(level)}
                      onChange={(e) => handleLoyaltyLevelChange(level, e.target.checked)}
                      sx={{
                        color: getLevelColor(level),
                        '&.Mui-checked': {
                          color: getLevelColor(level),
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getLevelIcon(level)}</span>
                      <span style={{ textTransform: 'capitalize' }}>{level}</span>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
            {formData.loyaltyLevels.length === 0 && (
              <Typography variant="caption" color="error">
                Please select at least one loyalty level
              </Typography>
            )}
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Product Image
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleImageChange}
                disabled={compressing}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={compressing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  fullWidth
                  disabled={compressing}
                >
                  {compressing ? 'Processing Image...' : 'Upload Image'}
                </Button>
              </label>
            </Box>
            {compressing && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                🗜️ Optimizing image for better performance...
              </Typography>
            )}
            {formData.imageFile && !compressing && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
                ✅ Image ready ({(formData.imageFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
            {imagePreview && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                  }}
                />
              </Box>
            )}
          </Grid>

          {/* Download File Upload */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Download File
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                style={{ display: 'none' }}
                id="download-upload"
                type="file"
                onChange={handleDownloadFileChange}
              />
              <label htmlFor="download-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Upload File
                </Button>
              </label>
            </Box>
            {formData.downloadFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {formData.downloadFile.name}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
